import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './LoadingScreen.css';

/* ------------------------------------------------------------------ */
/*  CONFIG                                                             */
/* ------------------------------------------------------------------ */
const VIDEO_DESKTOP = '/videos/loading-intro-desktop.mp4';
const VIDEO_MOBILE = '/videos/loading-intro-mobile.mp4';

// portrait screen (width < height) -> mobile video, warna desktop video
const pickVideo = () =>
  window.innerWidth < window.innerHeight ? VIDEO_MOBILE : VIDEO_DESKTOP;

const HOLD = 400; 
const EXIT = 600; 
const FAILSAFE = 9000; 
const EDGE_SAMPLES = 192; 


const LoadingScreen = ({ onLoadingComplete }) => {
  const [isExiting, setIsExiting] = useState(false);
  const [videoSrc] = useState(pickVideo);
  const startedExit = useRef(false);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const edgeV = useRef(null); 
  const edgeH = useRef(null); 

  // keep latest callback without restarting anything
  const doneRef = useRef(onLoadingComplete);
  useEffect(() => {
    doneRef.current = onLoadingComplete;
  }, [onLoadingComplete]);

  const finish = useCallback(() => {
    if (startedExit.current) return;
    startedExit.current = true;
    setTimeout(() => setIsExiting(true), HOLD);
    setTimeout(() => {
      if (doneRef.current) doneRef.current();
    }, HOLD + EXIT);
  }, []);

  // never leave the visitor stuck on the loader
  useEffect(() => {
    const t = setTimeout(finish, FAILSAFE);
    return () => clearTimeout(t);
  }, [finish]);

  /* ----- draw: video + extended edges on a canvas ----- */
  const draw = useCallback(() => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c || !v.videoWidth || !v.videoHeight) return;

    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = Math.round(window.innerWidth * dpr);
    const H = Math.round(window.innerHeight * dpr);
    if (c.width !== W || c.height !== H) {
      c.width = W;
      c.height = H;
    }
    const ctx = c.getContext('2d', { alpha: false });
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const vw = v.videoWidth;
    const vh = v.videoHeight;

    // poora video fit (contain). Zyada jagah ho to upar/side se extend.
    const s = Math.min(W / vw, H / vh);
    const dw = vw * s;
    const dh = vh * s;
    const dx = (W - dw) / 2;
    const dy = H - dh; 

    // extension strips (video ke kinare ke 2px, smooth karke)
    if (!edgeV.current) {
      edgeV.current = document.createElement('canvas');
      edgeV.current.width = 1;
      edgeV.current.height = EDGE_SAMPLES;
      edgeH.current = document.createElement('canvas');
      edgeH.current.width = EDGE_SAMPLES;
      edgeH.current.height = 1;
    }
    const ev = edgeV.current.getContext('2d');
    const eh = edgeH.current.getContext('2d');
    ev.imageSmoothingQuality = 'high';
    eh.imageSmoothingQuality = 'high';

    if (dx > 0.5) {
      // left
      ev.drawImage(v, 0, 0, 2, vh, 0, 0, 1, EDGE_SAMPLES);
      ctx.drawImage(edgeV.current, 0, 0, 1, EDGE_SAMPLES, 0, dy, dx + 1, dh);
      // right
      ev.drawImage(v, vw - 2, 0, 2, vh, 0, 0, 1, EDGE_SAMPLES);
      ctx.drawImage(edgeV.current, 0, 0, 1, EDGE_SAMPLES, dx + dw - 1, dy, dx + 2, dh);
    }
    if (dy > 0.5) {
      // top
      eh.drawImage(v, 0, 0, vw, 2, 0, 0, EDGE_SAMPLES, 1);
      ctx.drawImage(edgeH.current, 0, 0, EDGE_SAMPLES, 1, dx, 0, dw, dy + 1);
    }

    // asli video upar
    ctx.drawImage(v, dx, dy, dw, dh);
  }, []);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return undefined;
    let stopped = false;
    let rafId = 0;
    let vfcId = 0;
    const hasVFC = 'requestVideoFrameCallback' in v;

    const loop = () => {
      if (stopped) return;
      draw();
      if (hasVFC) vfcId = v.requestVideoFrameCallback(loop);
      else rafId = requestAnimationFrame(loop);
    };
    loop();

    const onResize = () => draw();
    window.addEventListener('resize', onResize);
    v.addEventListener('loadeddata', draw);

    return () => {
      stopped = true;
      cancelAnimationFrame(rafId);
      if (hasVFC && v.cancelVideoFrameCallback) v.cancelVideoFrameCallback(vfcId);
      window.removeEventListener('resize', onResize);
      v.removeEventListener('loadeddata', draw);
    };
  }, [draw]);

  return (
    <AnimatePresence>
      {!isExiting && (
        <motion.div
          className="ls"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.06 }}
          transition={{ duration: EXIT / 1000, ease: 'easeInOut' }}
        >
          <canvas ref={canvasRef} className="ls-canvas" />

          {/* asli video yahan chalta hai (dikhta nahi), canvas ispe se draw karta hai */}
          <video
            ref={videoRef}
            className="ls-video"
            src={videoSrc}
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={finish}
            onError={finish}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default LoadingScreen;
